import { api } from "./client.js";
import type { Bot, EmbedConfig, WorkflowConfig, WebSource, WebSourceMode } from "@chatfab/shared";

const D = "/api/v1/dashboard";

export const botsApi = {
  list: () => api.get<Bot[]>(`${D}/bots`),

  create: (body: { name: string; system_prompt?: string; description?: string }) =>
    api.post<Bot>(`${D}/bots`, body),

  get: (id: string) => api.get<Bot>(`${D}/bots/${id}`),

  update: (id: string, body: Partial<Bot>) => api.put<Bot>(`${D}/bots/${id}`, body),

  delete: (id: string) => api.delete<void>(`${D}/bots/${id}`),

  restore: (id: string) => api.post<void>(`${D}/bots/${id}/restore`),

  setState: (id: string, state: string) => api.put<void>(`${D}/bots/${id}/state`, { state }),

  setSlug: (id: string, slug: string) => api.put<{ slug: string }>(`${D}/bots/${id}/slug`, { slug }),

  // Documents
  getUploadUrl: (id: string, filename: string, content_type: string, size_bytes: number) =>
    api.post<{ upload_url: string; key: string }>(`${D}/bots/${id}/documents`, { filename, content_type, size_bytes }),

  listDocuments: (id: string) =>
    api.get<{ documents: Array<{ filename: string; size_bytes: number; last_modified: string }>; total_chunks: number }>(`${D}/bots/${id}/documents`),

  deleteDocument: (id: string, filename: string) =>
    api.delete<void>(`${D}/bots/${id}/documents/${encodeURIComponent(filename)}`),

  reingestDocument: (id: string, filename: string) =>
    api.post<void>(`${D}/bots/${id}/documents/${encodeURIComponent(filename)}/reingest`),

  // Invites
  generateInvite: (id: string, opts: { max_uses?: number; expires_at?: string }) =>
    api.post<{ code: string; code_prefix: string; max_uses: number }>(`${D}/bots/${id}/invites`, opts),

  listInvites: (id: string) =>
    api.get<Array<{ code_prefix: string; max_uses: number; current_uses: number; created_at: string; is_revoked: boolean; expires_at?: string }>>(`${D}/bots/${id}/invites`),

  revokeInvite: (id: string, prefix: string) =>
    api.delete<void>(`${D}/bots/${id}/invites/${prefix}`),

  // Embed
  getEmbed: (id: string) =>
    api.get<{ embed_config: EmbedConfig; snippet: string }>(`${D}/bots/${id}/embed`),

  updateEmbed: (id: string, config: Partial<EmbedConfig>) =>
    api.put<{ embed_config: EmbedConfig; snippet: string }>(`${D}/bots/${id}/embed`, config),

  // Analytics
  getAnalytics: (id: string) =>
    api.get<{ period: string; document_count: number; total_chunks: number; messages_used: number; messages_limit: number; tokens_used_input: number; tokens_used_output: number; daily_usage: Array<{ date: string; messages: number; tokens_input: number; tokens_output: number }> }>(`${D}/bots/${id}/analytics`),

  // Conversations
  listSessions: (id: string) =>
    api.get<{ sessions: Array<{ session_id: string; created_at: string; last_active_at: string; message_count: number; session_type: string }> }>(`${D}/bots/${id}/sessions`),

  // Playground
  createTestSession: (id: string) =>
    api.post<{ session_id: string }>(`${D}/bots/${id}/test-session`),

  // Workflow
  getWorkflow: (id: string) =>
    api.get<{ workflow: WorkflowConfig | null; tier: string; tier_trigger_limit: number; allowed_destinations: string[] }>(`${D}/bots/${id}/workflow`),

  saveWorkflow: (id: string, workflow: WorkflowConfig) =>
    api.put<WorkflowConfig>(`${D}/bots/${id}/workflow`, workflow),

  listHandoffs: (id: string) =>
    api.get<{ handoffs: Array<{ bot_id: string; session_id: string; trigger_id: string; trigger_intent: string; variables: Record<string, string>; conversation_summary?: string; created_at: string }> }>(`${D}/bots/${id}/handoffs`),

  // Web Sources
  listWebSources: (id: string) =>
    api.get<{ web_sources: WebSource[] }>(`${D}/bots/${id}/websources`),

  createWebSource: (id: string, body: { name: string; mode: WebSourceMode; urls?: string[]; seed_url?: string; max_depth?: number; max_pages?: number }) =>
    api.post<{ web_source: WebSource }>(`${D}/bots/${id}/websources`, body),

  deleteWebSource: (id: string, websourceId: string) =>
    api.delete<void>(`${D}/bots/${id}/websources/${websourceId}`),

  recrawlWebSource: (id: string, websourceId: string) =>
    api.post<{ success: boolean }>(`${D}/bots/${id}/websources/${websourceId}/recrawl`),
};
